import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  Tooltip,
  Button,
  Grid,
  Paper,
  Link,
  Divider,
  Switch 
} from '@mui/material';
import {
  HelpOutline as HelpOutlineIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  LocalFireDepartment as FlameIcon,
  InfoOutlined as InfoIcon,
  EditOutlined as EditIcon,
  Whatshot as StreakFreezeIcon,
  // HeartBrokenOutlined as StreakFreezeIcon
} from '@mui/icons-material';
import PrimaryIcon from "Components/Common/PrimaryIcon";
import { ReactComponent as CloseIcon } from "Assets/Icons/Home/CloseIcon.svg";
import { getStreakScore } from 'Slices/thunks';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import useTimer from 'Hooks/useTimer';

const styles = (theme) => ({
  // m: 0,
  // p: 2,
  // display: 'flex',
  // alignItems: 'center',
  "& .switch": {
    px: 2,
    py: 0,
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    color: theme.palette.secondary.main,
    backgroundColor: theme.palette.white.main,
    border: `1px solid ${theme.palette.primary.main}`,
    borderRadius: "8px",
    maxHeight: "32px"
  }
});

// --- Helper Components for Dynamic Calendar ---

const formatDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const TooltipBox = React.memo(({score, points, day, date, Translation, children}) => {
  return (
    <Tooltip
      arrow
      followCursor
      title={
        <Box>
          <Box display={"flex"} justifyContent={"center"} alignItems={"center"}>
            {`${score}/${points || 0}`}
          </Box>
          <Divider color="white" sx={{ my: 0.5 }} />
          <Box display={"flex"} justifyContent={"center"} gap="8px">
            <Typography variant="body2">{`${day} ${date.toLocaleDateString('default', { month: 'long' })}`}</Typography>
            {/* <Divider color="white" flexItem orientation="vertical" /> */}
            <Typography variant="body2">
              {score >= points ? Translation?.Achieved : 
                score ? Translation?.Halved : Translation?.Missed}
            </Typography>
          </Box>
        </Box>
      }
    >
      {children}
    </Tooltip>

  )
})

const CalendarHeader = ({ date, onPrev, onNext, Translation }) => (
  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
    <IconButton size="small" onClick={onPrev}>
      <ChevronLeftIcon sx={(theme) => ({transform: theme.direction === "rtl" ? "scaleX(-1)" : "none"})} />
    </IconButton>
    <Typography variant="h6" fontWeight="bold">
      {Translation[date.toLocaleString('default', { month: 'long' })] || date.toLocaleString('default', { month: 'long' })} {date.getFullYear()}
    </Typography>
    <IconButton size="small" onClick={onNext}>
      <ChevronRightIcon sx={(theme) => ({transform: theme.direction === "rtl" ? "scaleX(-1)" : "none"})} />
    </IconButton>
  </Box>
);

const CalendarGrid = ({ date }) => {
  // const weekDays = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'];
  const { TodayScore, StreakScore, Translation } = useSelector((state) => ({
    TodayScore: state.Dashboard.todayScore,
    StreakScore: state.Dashboard.streakScore,
    Translation: state.Translation.translation?.Dashboard || {},
  }));
  
  const startDate = useMemo(() => {
    const today = new Date();
    if (!TodayScore) return today;

    const startDate = new Date();
    const endDate = new Date();

    startDate.setDate(startDate.getDate() - TodayScore?.StartDay);
    endDate.setDate(endDate.getDate() - TodayScore?.EndDay);

    const startTime = new Date(`${formatDate(startDate)} ${TodayScore?.StartTime}`);
    const endTime = new Date(`${formatDate(startDate)} ${TodayScore?.EndTime}`);

    if (startTime.getTime() > today.getTime()) {
      startDate.setDate(startDate.getDate() - 1);
      return startDate;

    } else if (today.getTime() > startTime.getTime() && today.getTime() < endTime.getTime()) {
      return startDate;
    }

    if (TodayScore?.StartDay) {
      startDate.setDate(startDate.getDate() + TodayScore?.StartDay);
    }

    return new Date(`${formatDate(startDate)} ${TodayScore?.StartTime}`);
  }, [TodayScore]);

  const weekDays = useMemo(() => {
    return [Translation?.Mon, Translation?.Tue, Translation?.Wed, Translation?.Thu, Translation?.Fri, Translation?.Sat, Translation?.Sun,];
  }, [Translation])
  
  const today = new Date();
  // today.setHours(0, 0, 0, 0);
  const year = date.getFullYear();
  const month = date.getMonth();

  // --- Dynamic Calendar Logic ---
  const firstDayOfMonth = new Date(year, month, 1).getDay();
  const startDayIndex = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const emptyDays = Array(startDayIndex).fill(null);
  const monthDays = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const calendarCells = [...emptyDays, ...monthDays];


  const styles = (theme) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    width: 40,
    height: 40,
    backgroundColor: theme.palette.background.default,
    borderRadius: '50%',
    m: 'auto',
    // cursor: 'pointer',
    "& .disabled": {
      width: "25px",
      height: "25px",
      display: "flex",
      alignItems: 'center',
      justifyContent: "center",
      border: `1px solid ${theme.palette.border.blueLight}`,
      borderRadius: '50%',
      cursor: "pointer"
    },
    "& .todayDate": {
      width: "25px",
      height: "25px",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      backgroundColor: theme.palette.primary.main,
      borderRadius: '50%',
      cursor: "pointer",
      "& span": {
        width: "20px",
        height: "20px",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        border: `3px solid ${theme.palette.white.main}`,
        borderRadius: '50%',
        cursor: "pointer"
      }
    }
  });

  return (
    <Grid container columns={7} spacing={1}>
      {weekDays.map((day) => (
        <Grid item xs={1} key={day}>
          <Typography variant="title" color="secondary.main" sx={{ display: 'block', textAlign: 'center', fontSize: '14px', fontWeight: 500 }}>
            {day}
          </Typography>
        </Grid>
      ))}
      {calendarCells.map((day, index) => {
        const d1 = day ? new Date(year, month, day) : new Date(1970, 0, 1);
        d1.setHours(0, 0, 0, 0);
        const d2 = `${d1.getFullYear()}-${String(d1.getMonth() + 1).padStart(2, '0')}-${String(d1.getDate()).padStart(2, '0')}`
        const score =  StreakScore[d2]?.Score || 0;
        const points =  StreakScore[d2]?.Points;

        return (
        <Grid item xs={1} key={index} sx={{ mb: 1 }}>
          {day && (
            <Box sx={styles}>
              { startDate >= d1 ?

                <> 
                  {(score >= points && startDate.toDateString() != d1.toDateString()) ?
                    <TooltipBox score={score} points={points} day={day} date={d1} Translation={Translation}>
                      <FlameIcon sx={{ color: '#FF9900', fontSize: 28 }} />
                    </TooltipBox> :
                    (score && startDate.toDateString() != d1.toDateString()) ? 
                    <TooltipBox score={score} points={points} day={day} date={d1} Translation={Translation}>
                      <StreakFreezeIcon sx={{ color: '#FF9900', fontSize: 28 }} />
                    </TooltipBox> :
                    <Box className="disabled">
                      <TooltipBox score={score} points={points} day={day} date={d1} Translation={Translation}>
                        { (startDate.toDateString() == d1.toDateString()) ?
                          <Typography className="todayDate">
                            <Typography variant="span"></Typography>
                          </Typography> :
                          <Typography variant="body1" alignItems={"center"}>{day}</Typography> 
                        }
                      </TooltipBox>
                    </Box>
                  }
                </> : 
                startDate > d1 ?
                <Box className="disabled">
                  <Typography variant="body1" alignItems={"center"}>{day}</Typography> 
                </Box> :
                <Typography variant="body1" alignItems={"center"}>{day}</Typography> 
              }
            </Box>
          )}
        </Grid>
      )})}
    </Grid>
  );
};


// --- Main Component ---

const StreakDialog = ({ open, setOpen, setGoalOpen, setStreakModeOpen }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { remainingTime } = useTimer();
  const courseId = localStorage.getItem("CourseID");
  const [currentDate, setCurrentDate] = useState(new Date());

  const { Profile, Courses, TodayScore, Translation } = useSelector((state) => ({
    Profile: state.Profile.profile,
    Courses: state.Course.courses,
    TodayScore: state.Dashboard.todayScore,
    Translation: state.Translation.translation?.Dashboard || {},
  }));

  const handleModeChange = (event) => {
    setOpen(false);
    setStreakModeOpen(true);
  }

  const handlePrevMonth = () => {
    // dispatch(getStreakScore({
    //   CourseID: courseId,
    //   Year: currentDate.getFullYear(),
    //   Month: currentDate.getMonth() - 1
    // }))
    setCurrentDate(prevDate => new Date(prevDate.getFullYear(), prevDate.getMonth() - 1, 1));
  };
  
  const handleNextMonth = () => {
    // dispatch(getStreakScore({
    //   CourseID: courseId,
    //   Year: currentDate.getFullYear(),
    //   Month: currentDate.getMonth() + 1
    // }))
    setCurrentDate(prevDate => new Date(prevDate.getFullYear(), prevDate.getMonth() + 1, 1));
  };

  const handleStartSolving = () => {
    if (!courseId) {
      if (Array.isArray(Courses) && Courses.length == 0) {
        return
      }
      localStorage.setItem('CourseID', Courses[0]?.ID)
    }
    navigate('/problems')
    setOpen(false);
  }

  return (
    <Dialog
      open={open}
      onClose={setOpen}
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: '16px',
          width: '800px',
        },
      }}
      sx={styles}
    >
      <DialogTitle sx={{m: 0, p:2, display: 'flex', alignItems: 'center'}}>
        <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 'bold' }}>
          {Translation?.ProblemSolvingStreak}
          <IconButton size="small" sx={{ ml: 1, color: 'secondary.light' }}>
            <HelpOutlineIcon sx={{ color: 'white.main' }} />
          </IconButton>
        </Typography>
        <Box className="switch">
          <Typography variant="body2">{Translation?.Daily}</Typography>
           <Switch color="primary" checked={Profile?.IsWeekly} onChange={handleModeChange} />
          <Typography variant="body2">{Translation?.Weekly}</Typography>
        </Box>
        <IconButton onClick={() => setOpen()}>
          {" "}
          <PrimaryIcon
            icon={CloseIcon}
            color="white"
            height="14px"
            width="14px"
            cursor="pointer"
          />{" "}
        </IconButton>{" "}
      </DialogTitle>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3, py: 2, display: 'flex', alignItems: 'center' }} gap="28px">
        {/* <Tabs value={tab} onChange={handleTabChange} aria-label="streak tabs">
          <Tab label="Stats" sx={{ textTransform: 'none' }} />
          <Tab label="Streak Calendar" sx={{ textTransform: 'none', fontWeight: 'bold' }} />
        </Tabs> */}
        <Box display={"flex"} justifyContent={"space-between"} alignItems={"center"} width={"230px"}>
          <Typography variant="body2" color="secondary.main" fontSize={"14px"} fontWeight={"600"}>{Translation?.Stats}</Typography>
          <Typography variant="body2" color="secondary.light">
            {/* {Translation?.ResetsIn} 10h 24m 28s */}
            {Translation?.ResetsIn} {remainingTime}
          </Typography>
        </Box>
        <Typography variant="body2" color="secondary.main" fontSize={"14px"} fontWeight={"600"}>{Translation?.StreakCalendar}</Typography>
      </Box>

      <DialogContent sx={{ bgcolor: '#F7F8FA', p: 3 }}>
        <Grid container spacing={3}>
          {/* Left Column */}
          <Grid item xs={4}>
            <Box display="flex" flexDirection="column" gap={2}>
              <Paper elevation={0} sx={{ p: 2, borderRadius: '12px' }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="body1" color="secondary.light">
                      {Translation?.CurrentStreak}
                    </Typography>
                    <Typography variant="h3" fontWeight="500">
                      {TodayScore?.CurrentStreaks}
                    </Typography>
                  </Box>
                  <FlameIcon sx={{ fontSize: 40, color: '#FF9900' }} />
                </Box>
              </Paper>
              <Paper elevation={0} sx={{ p: 2, borderRadius: '12px' }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="body1" fontWeight="500">
                    {Translation?.DailyGoalProgress}
                  </Typography>
                  <Box>
                    <InfoIcon fontSize="small" />
                    <IconButton size="small" 
                    onClick={() => {
                      setGoalOpen(true);
                      setOpen(false);
                    }}><EditIcon fontSize="small" /></IconButton>
                  </Box>
                </Box>
                <Typography variant="h3" fontWeight="500">
                  {TodayScore?.TodayPoints}<Typography component="span" variant="h5" color="secondary.light">/{TodayScore?.TotalPoints} {Translation?.Pts}</Typography>
                </Typography>
              </Paper>
              <Paper variant="outlined" sx={{ p: 2, display: 'flex', gap: 1, alignItems: 'start', borderColor: '#E0E0E0', borderRadius: '12px', bgcolor: 'transparent' }}>
                <InfoIcon color="action" />
                <Typography variant="body2" color="secondary.light">
                  {Translation?.GetStreakCoins}
                </Typography>
              </Paper>
            </Box>
          </Grid>
          {/* Right Column (Calendar) */}
          <Grid item xs={8}>
            <Paper elevation={0} sx={{ p: 2, borderRadius: '12px' }}>
              <CalendarHeader date={currentDate} onPrev={handlePrevMonth} onNext={handleNextMonth} Translation={Translation} />
              <CalendarGrid date={currentDate} />
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between', p: 2, borderTop: '1px solid #E0E0E0' }}>
        <Box display={"flex"} alignItems={"center"} gap="24px">
          <Box display={"flex"} alignItems={"center"} gap="8px">
            <Link underline="hover" fontSize={"14px"} fontWeight={600} sx={{ cursor: 'pointer' }}>
              {Translation?.BuyStreakFreeze}
            </Link>
            <Typography variant="body2" color="secondary.main" fontSize={"14px"} fontWeight={600}>00</Typography>
          </Box>
          <Divider orientation="vertical" color="secondary.main" flexItem mx={2} />
          <Link underline="hover" fontSize={"14px"} fontWeight={600} sx={{ cursor: 'pointer' }} onClick={() => window.open(Translation?.CheckFAQLink, '_blank')}>
            {Translation?.CheckFAQs}
          </Link>
        </Box>
        <Button variant="contained" sx={{ textTransform: 'none', borderRadius: '20px', ml: 2 }} onClick={handleStartSolving}>
          {Translation?.StartSolving}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StreakDialog;